@if (isWindowMode && _active) {
  <kendo-window (close)="onClose()" [title]="reportName || 'Forecast Report'"
    (stateChange)="onStateChanges($event)" [(state)]="windowState"
    class="forecastForm reportForecastingWindow">
    <ng-container *ngTemplateOutlet="forecastContentTpl"></ng-container>
  </kendo-window>
}

@if (!isWindowMode) {
  <ng-container *ngTemplateOutlet="forecastContentTpl"></ng-container>
}

<ng-template #forecastContentTpl>
  <div class="forecast-viewer-root" [class.window-mode]="isWindowMode">
    <!-- LOADING OVERLAY -->
    @if (loading) {
      <div class="forecast-loading-overlay">
        <div class="forecast-spinner"></div>
        <span>Loading Forecast Data...</span>
      </div>
    }

    <!-- FORECAST TOP TOOLBAR -->
    <div class="forecast-top-bar">
      <div class="bar-left-section">
        <div class="barcloud-brand-title">
          <span class="brand-barcloud">BARCLOUD</span>
          <span class="brand-separator">|</span>
          <span class="brand-sub">FORECAST</span>
        </div>

        <div class="filters-cluster">
          <span class="filter-label">Filter by:</span>

          <label class="filter-badge-item" [class.badge-active]="filterForecast">
            <input type="checkbox" kendoCheckBox [(ngModel)]="filterForecast" (ngModelChange)="applyFilters()" />
            <span class="badge-tag tag-forecast-badge">forecast</span>
          </label>

          <label class="filter-badge-item" [class.badge-active]="filterHistorical">
            <input type="checkbox" kendoCheckBox [(ngModel)]="filterHistorical" (ngModelChange)="applyFilters()" />
            <span class="badge-tag tag-historical-badge">historical Data</span>
          </label>

          <span class="v-divider">|</span>

          <label class="filter-chk-item">
            <input type="checkbox" kendoCheckBox [(ngModel)]="useSystemDecimals" />
            <span class="chk-text">Use System Configuration Decimals for Qty(s)</span>
          </label>

          <span class="v-divider">|</span>

          <label class="filter-chk-item">
            <input type="checkbox" kendoCheckBox [(ngModel)]="hideZeroQtyForecast" (ngModelChange)="applyFilters()" />
            <span class="chk-text">Hide 0 Qty Forecast values</span>
          </label>
        </div>
      </div>

      <div class="bar-right-section">
        <button kendoButton class="toolbar-btn btn-excel" (click)="exportToExcel()">
          <span class="k-icon k-i-file-excel excel-icon"></span>
          Export to Excel
        </button>

        <button kendoButton class="toolbar-btn btn-chart-toggle" (click)="toggleChart()">
          <span class="k-icon" [ngClass]="showChart ? 'k-i-grid' : 'k-i-chart-line'"></span>
          {{ showChart ? 'Show Grid' : 'Show Chart' }}
        </button>

        @if (isWindowMode) {
          <button kendoButton class="toolbar-btn btn-close-x" (click)="onClose()" title="Close">
            <span class="k-icon k-i-close"></span>
            Close
          </button>
        }
      </div>
    </div>

    <!-- MAIN VIEWPORT: GRID VIEW -->
    @if (!showChart) {
      <div class="forecast-main-body">
        <kendo-grid
          #forecastGrid="kendoGrid"
          [data]="gridView"
          [pageSize]="gridState.take"
          [skip]="gridState.skip"
          [sort]="gridState.sort"
          [sortable]="true"
          [pageable]="{ buttonCount: 5, info: true, type: 'numeric', pageSizes: [25, 50, 100, 200] }"
          [groupable]="{ emptyText: 'Drag a column header and drop it here to group by that column' }"
          [group]="gridState.group"
          (dataStateChange)="onGridStateChange($event)"
          class="forecast-data-grid"
          >
          <kendo-grid-column field="stockName" title="Stock Name" [width]="170">
            <ng-template kendoGridCellTemplate let-dataItem>
              <span class="stock-name-cell">{{ dataItem.stockName }}</span>
            </ng-template>
          </kendo-grid-column>
          <kendo-grid-column field="date" title="Date" [width]="130">
            <ng-template kendoGridCellTemplate let-dataItem>
              <span>{{ dataItem.date }}</span>
            </ng-template>
          </kendo-grid-column>
          <kendo-grid-column field="qty" title="Qty" [width]="90" class="col-numeric">
            <ng-template kendoGridCellTemplate let-dataItem>
              <span [class.forecast-val]="dataItem.dataSource === 'Forecast'">
                {{ useSystemDecimals ? (dataItem.qty | number:'1.2-2') : dataItem.qty }}
              </span>
            </ng-template>
          </kendo-grid-column>
          <kendo-grid-column field="qtyPendingSO" title="Qty Pending on SO" [width]="140" class="col-numeric">
            <ng-template kendoGridCellTemplate let-dataItem>
              {{ useSystemDecimals ? (dataItem.qtyPendingSO | number:'1.2-2') : dataItem.qtyPendingSO }}
            </ng-template>
          </kendo-grid-column>
          <kendo-grid-column field="qtyPendingPO" title="Qty Pending on PO" [width]="140" class="col-numeric">
            <ng-template kendoGridCellTemplate let-dataItem>
              {{ useSystemDecimals ? (dataItem.qtyPendingPO | number:'1.2-2') : dataItem.qtyPendingPO }}
            </ng-template>
          </kendo-grid-column>
          <kendo-grid-column field="vendor" title="Vendor" [width]="140">
            <ng-template kendoGridCellTemplate let-dataItem>
              <span>{{ dataItem.vendor }}</span>
            </ng-template>
          </kendo-grid-column>
          <kendo-grid-column field="estimatedCost" title="Estimated Cost" [width]="150" class="col-numeric">
            <ng-template kendoGridCellTemplate let-dataItem>
              {{ dataItem.estimatedCost | number:'1.3-3' }}
            </ng-template>
            <ng-template kendoGridFooterTemplate>
              <div class="grid-footer-aggregate">Sum: {{ totalEstimatedCost | number:'1.3-3' }}</div>
            </ng-template>
          </kendo-grid-column>
          <kendo-grid-column field="estimatedPrice" title="Estimated Price" [width]="150" class="col-numeric">
            <ng-template kendoGridCellTemplate let-dataItem>
              {{ dataItem.estimatedPrice | number:'1.3-3' }}
            </ng-template>
            <ng-template kendoGridFooterTemplate>
              <div class="grid-footer-aggregate">Sum: {{ totalEstimatedPrice | number:'1.3-3' }}</div>
            </ng-template>
          </kendo-grid-column>
          <kendo-grid-column field="qtyOnHand" title="Qty On Hand" [width]="110" class="col-numeric">
            <ng-template kendoGridCellTemplate let-dataItem>
              {{ useSystemDecimals ? (dataItem.qtyOnHand | number:'1.2-2') : dataItem.qtyOnHand }}
            </ng-template>
          </kendo-grid-column>
          <kendo-grid-column field="currentSuggestedReorder" title="Current Suggested Reorder" [width]="180" class="col-numeric">
            <ng-template kendoGridCellTemplate let-dataItem>
              <span [class.negative-reorder]="dataItem.currentSuggestedReorder < 0" [class.positive-reorder]="dataItem.currentSuggestedReorder > 0">
                {{ dataItem.currentSuggestedReorder }}
              </span>
            </ng-template>
          </kendo-grid-column>
          <kendo-grid-column field="dataSource" title="Data Source" [width]="130">
            <ng-template kendoGridCellTemplate let-dataItem>
              <span class="source-badge" [ngClass]="dataItem.dataSource === 'Forecast' ? 'src-forecast' : 'src-historical'">
                {{ dataItem.dataSource }}
              </span>
            </ng-template>
          </kendo-grid-column>
          <kendo-grid-excel fileName="Barcloud_Forecast_Report.xlsx"></kendo-grid-excel>
        </kendo-grid>
      </div>
    }

    <!-- MAIN VIEWPORT: CHART VIEW -->
    @if (showChart) {
      <div class="forecast-main-body forecast-chart-view">
        <div class="chart-container-card">
          <kendo-chart style="height: 100%; min-height: 460px; width: 100%;">
            <kendo-chart-title text="Demand & Forecast Trend Analysis" color="#0f172a" font="bold 16px sans-serif"></kendo-chart-title>
            <kendo-chart-legend position="top" orientation="horizontal"></kendo-chart-legend>
            <kendo-chart-tooltip [shared]="true">
              <ng-template kendoChartSeriesTooltipTemplate let-value="value" let-category="category" let-series="series">
                {{ category }} - {{ series.name }}: <strong>{{ value | number:'1.0-2' }}</strong>
              </ng-template>
            </kendo-chart-tooltip>
            <kendo-chart-category-axis>
              <kendo-chart-category-axis-item [categories]="chartCategories">
                <kendo-chart-category-axis-item-labels [rotation]="-35"></kendo-chart-category-axis-item-labels>
              </kendo-chart-category-axis-item>
            </kendo-chart-category-axis>
            <kendo-chart-value-axis>
              <kendo-chart-value-axis-item [title]="{ text: 'Quantity' }"></kendo-chart-value-axis-item>
            </kendo-chart-value-axis>
            <kendo-chart-series>
              @for (s of chartSeries; track s) {
                <kendo-chart-series-item
                  type="line"
                  [style]="'smooth'"
                  [data]="s.data"
                  [name]="s.name"
                  [color]="s.color"
                  >
                </kendo-chart-series-item>
              }
            </kendo-chart-series>
          </kendo-chart>
        </div>
      </div>
    }
  </div>
</ng-template>
